import { create } from "zustand";
import { persist } from "zustand/middleware";

type Locale = "en" | "sr";
type Theme = "light" | "dark" | "system";

interface SettingsState {
  // Game settings
  showHints: boolean;
  confirmZeroScore: boolean;

  // Display settings
  locale: Locale;
  theme: Theme;

  // Actions
  setShowHints: (show: boolean) => void;
  setConfirmZeroScore: (confirm: boolean) => void;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  resetSettings: () => void;
}

const defaultSettings = {
  showHints: true,
  confirmZeroScore: true,
  locale: "en" as Locale,
  theme: "system" as Theme,
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...defaultSettings,

      setShowHints: (show) => set({ showHints: show }),
      setConfirmZeroScore: (confirm) => set({ confirmZeroScore: confirm }),
      setLocale: (locale) => set({ locale }),
      setTheme: (theme) => set({ theme }),
      resetSettings: () => set(defaultSettings),
    }),
    {
      name: "yamber-settings",
    }
  )
);

// Localization strings
export const TRANSLATIONS: Record<Locale, Record<string, string>> = {
  en: {
    // Categories
    ones: "Ones",
    twos: "Twos",
    threes: "Threes",
    fours: "Fours",
    fives: "Fives",
    sixes: "Sixes",
    max: "Max",
    min: "Min",
    straight: "Straight",
    fullHouse: "Full House",
    poker: "Poker",
    yamb: "Yamb",

    // Columns
    down: "Down",
    up: "Up",
    free: "Free",
    announce: "Announce",

    // UI
    rollDice: "Roll Dice",
    rollAgain: "Roll Again",
    score: "Score",
    total: "Total",
    newGame: "New Game",
    quitGame: "Quit Game",
    gameOver: "Game Over!",
    finalScore: "Final Score",
    settings: "Settings",
    stats: "Stats",
    history: "History",
    login: "Login",
    logout: "Logout",
    register: "Register",
    guest: "Play as Guest",
    // Theme
    theme: "Theme",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    themeDescription: "Choose your preferred color scheme",
  },
  sr: {
    // Categories
    ones: "Jedinice",
    twos: "Dvojke",
    threes: "Trojke",
    fours: "Četvorke",
    fives: "Petice",
    sixes: "Šestice",
    max: "Maks",
    min: "Min",
    straight: "Kenta",
    fullHouse: "Ful",
    poker: "Poker",
    yamb: "Jamb",

    // Columns
    down: "Dole",
    up: "Gore",
    free: "Slobodno",
    announce: "Najava",

    // UI
    rollDice: "Baci kocke",
    rollAgain: "Baci ponovo",
    score: "Rezultat",
    total: "Ukupno",
    newGame: "Nova igra",
    quitGame: "Izađi",
    gameOver: "Kraj igre!",
    finalScore: "Konačan rezultat",
    settings: "Podešavanja",
    stats: "Statistika",
    history: "Istorija",
    login: "Prijava",
    logout: "Odjava",
    register: "Registracija",
    guest: "Igraj kao gost",
    // Theme
    theme: "Tema",
    themeLight: "Svetla",
    themeDark: "Tamna",
    themeSystem: "Sistemska",
    themeDescription: "Izaberite željenu šemu boja",
  },
};

export function useTranslation() {
  const locale = useSettingsStore((state) => state.locale);

  return (key: string): string => {
    return TRANSLATIONS[locale][key] ?? key;
  };
}
